import { useEffect, useState, useCallback } from "react";
import { format } from "date-fns";
import { Search, Store } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";
import { Loader, EmptyState, PageHeader } from "@/components/common/Primitives";
import { StatusBadge } from "@/components/common/StatusBadge";
import { SetPasswordDialog } from "@/components/common/PasswordDialogs";

const EDITABLE = ["owner_name", "phone", "address", "city", "state", "pincode", "cuisine"];

function Details({ r, onClose, onSaved }) {
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (EDITABLE.some((k) => k !== "cuisine" && !String(edit[k] || "").trim())) return toast.error("Please fill in all fields");
    setBusy(true);
    try { const { data } = await api.patch(`/admin/restaurants/${r.id}/details`, edit); toast.success("Details updated"); setEdit(null); onSaved(data); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const rows = [["Restaurant", r.name], ["Email", r.email], ["Owner name", r.owner_name, "owner_name"], ["Mobile", r.phone, "phone"], ["Full address", r.address, "address"],
    ["City", r.city, "city"], ["State", r.state, "state"], ["PIN code", r.pincode, "pincode"], ["Cuisine", r.cuisine, "cuisine"],
    ["Registered", r.created_at && format(new Date(r.created_at), "dd MMM yyyy, hh:mm a")]];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="restaurant-details-dialog">
        <DialogHeader><DialogTitle className="font-heading">Restaurant details</DialogTitle></DialogHeader>
        <dl className="divide-y divide-slate-100 text-sm">
          {rows.map(([k, v, key]) => (
            <div key={k} className="flex items-center gap-3 py-2"><dt className="w-28 shrink-0 text-slate-400">{k}</dt>
              {edit && key ? <Input className="h-8" value={edit[key] || ""} onChange={(e) => setEdit({ ...edit, [key]: e.target.value })} data-testid={`edit-${key}`} />
                : <dd className="break-words text-slate-800" data-testid={`detail-${k.toLowerCase().replace(/ /g, "-")}`}>{v || "—"}</dd>}</div>
          ))}
          <div className="flex gap-3 py-2"><dt className="w-28 shrink-0 text-slate-400">Status</dt><dd><StatusBadge status={r.status} /></dd></div>
        </dl>
        <div className="flex justify-end gap-2">
          {edit ? <>
            <Button size="sm" variant="ghost" onClick={() => setEdit(null)} data-testid="details-cancel-btn">Cancel</Button>
            <Button size="sm" disabled={busy} onClick={save} className="bg-emerald-500 hover:bg-emerald-600" data-testid="details-save-btn">Save</Button>
          </> : <Button size="sm" variant="outline" onClick={() => setEdit(Object.fromEntries(EDITABLE.map((k) => [k, r[k] || ""])))} data-testid="details-edit-btn">Edit details</Button>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Manage({ r, plans, onClose, onDone }) {
  const [planId, setPlanId] = useState((plans.find((p) => p.price > 0) || plans[0])?.id || "");
  const [days, setDays] = useState(30);
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); onDone(); } catch (e) { toast.error(apiError(e)); } };
  const setStatus = (s) => act(() => api.patch(`/admin/restaurants/${r.id}/status`, { status: s }), `Restaurant ${s}`);
  const sub = (action) => act(() => api.post(`/admin/restaurants/${r.id}/subscription`, { action, plan_id: planId, days: Number(days) }), `Subscription: ${action}`);
  const s = r.subscription;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="manage-restaurant-dialog">
        <DialogHeader><DialogTitle className="font-heading">{r.name}</DialogTitle></DialogHeader>
        <div className="space-y-1 text-sm text-slate-600">
          <p>{r.email} · {r.phone || "no phone"}</p>
          <p>/r/{r.slug} · Registered {r.created_at && format(new Date(r.created_at), "dd MMM yyyy")}</p>
        </div>
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Account status: <StatusBadge status={r.status} /></p>
          <div className="flex flex-wrap gap-2">
            {r.status !== "active" && <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600" onClick={() => setStatus("active")} data-testid="approve-btn">{r.status === "pending" ? "Approve" : "Activate"}</Button>}
            {r.status === "pending" && <Button size="sm" variant="outline" onClick={() => setStatus("rejected")} data-testid="reject-btn">Reject</Button>}
            {r.status === "active" && <Button size="sm" variant="outline" className="text-rose-600" onClick={() => setStatus("suspended")} data-testid="suspend-btn">Suspend</Button>}
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 p-3 text-sm">
          <p className="mb-2 text-slate-600">Owner login: {r.email}</p>
          <SetPasswordDialog inline title="Reset owner password" testId="admin-reset-owner-pw"
            description="Set a temporary password and share it with the owner securely. All their current sessions will be signed out."
            onSubmit={(pw) => act(() => api.post(`/admin/restaurants/${r.id}/reset-owner-password`, { new_password: pw }), "Owner password reset")} />
        </div>
        <div className="rounded-xl bg-slate-50 p-4">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Subscription</p>
          {s ? <p className="mb-3 text-sm">{s.plan_name} <StatusBadge status={s.effective_status} /> · expires {format(new Date(s.expiry_date), "dd MMM yyyy")}</p> : <p className="mb-3 text-sm text-slate-500">None</p>}
          <div className="flex flex-wrap gap-2">
            <select className="h-9 rounded-md border border-slate-200 px-2 text-sm" value={planId} onChange={(e) => setPlanId(e.target.value)} data-testid="manual-plan-select">
              {plans.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <Input type="number" min="1" className="w-24" value={days} onChange={(e) => setDays(e.target.value)} data-testid="manual-days-input" />
            <Button size="sm" onClick={() => sub("activate")} data-testid="sub-activate-btn">Activate / extend</Button>
          </div>
          {s && (
            <div className="mt-2 flex gap-2">
              {s.status !== "suspended" ? <Button size="sm" variant="outline" onClick={() => sub("suspend")} data-testid="sub-suspend-btn">Suspend plan</Button>
                : <Button size="sm" variant="outline" onClick={() => sub("reactivate")} data-testid="sub-reactivate-btn">Reactivate</Button>}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminRestaurants() {
  const [data, setData] = useState(null);
  const [plans, setPlans] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [open, setOpen] = useState(null);
  const [view, setView] = useState(null);

  const load = useCallback(() => api.get("/admin/restaurants", { params: { search, status, limit: 100 } }).then((r) => setData(r.data)), [search, status]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/admin/plans").then((r) => setPlans(r.data.filter((p) => p.is_active))); }, []);

  return (
    <div>
      <PageHeader title="Restaurants" subtitle="Approve, suspend and manage subscriptions." />
      <div className="mb-5 flex flex-col gap-3 sm:flex-row">
        <div className="relative sm:w-72">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <Input className="pl-9" placeholder="Search name, email, city, mobile" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="admin-restaurant-search" />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {["", "pending", "active", "suspended", "rejected"].map((s) => (
            <button key={s || "all"} onClick={() => setStatus(s)} data-testid={`admin-filter-${s || "all"}`}
              className={`rounded-full px-3 py-1.5 text-xs font-medium capitalize transition-colors ${status === s ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-100"}`}>{({ "": "All", active: "Approved" })[s] ?? s}</button>
          ))}
        </div>
      </div>
      {!data ? <Loader /> : data.items.length === 0 ? <EmptyState icon={Store} title="No restaurants found" /> : (
        <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
          <table className="w-full text-sm" data-testid="admin-restaurants-table">
            <thead><tr className="text-left text-xs uppercase text-slate-400"><th className="p-3">Restaurant</th><th className="p-3">Owner</th><th className="p-3">City</th><th className="p-3">Registered</th><th className="p-3">Status</th><th className="p-3">Plan</th><th className="p-3">Expires</th><th className="p-3" /></tr></thead>
            <tbody>
              {data.items.map((r) => (
                <tr key={r.id} className="border-t border-slate-100" data-testid={`admin-restaurant-row-${r.slug}`}>
                  <td className="p-3"><p className="font-medium text-slate-800">{r.name}</p><p className="text-xs text-slate-400">{r.email}</p></td>
                  <td className="p-3"><p className="text-slate-700">{r.owner_name || "—"}</p><p className="text-xs text-slate-400">{r.phone || ""}</p></td>
                  <td className="p-3 text-slate-500">{[r.city, r.state].filter(Boolean).join(", ") || "—"}</td>
                  <td className="p-3 whitespace-nowrap text-slate-500">{r.created_at ? format(new Date(r.created_at), "dd MMM yyyy") : "—"}</td>
                  <td className="p-3"><StatusBadge status={r.status} /></td>
                  <td className="p-3">{r.subscription ? <span className="flex items-center gap-2">{r.subscription.plan_name}<StatusBadge status={r.subscription.effective_status} /></span> : "—"}</td>
                  <td className="p-3 text-slate-500">{r.subscription ? format(new Date(r.subscription.expiry_date), "dd MMM yyyy") : "—"}</td>
                  <td className="p-3 whitespace-nowrap text-right"><Button size="sm" variant="ghost" className="mr-1" onClick={() => setView(r)} data-testid={`admin-view-${r.slug}`}>View details</Button><Button size="sm" variant="outline" onClick={() => setOpen(r)} data-testid={`admin-manage-${r.slug}`}>Manage</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {view && <Details r={view} onClose={() => setView(null)} onSaved={(d) => { setView({ ...view, ...d }); load(); }} />}
      {open && <Manage r={open} plans={plans} onClose={() => setOpen(null)} onDone={() => { setOpen(null); load(); }} />}
    </div>
  );
}
